/**
 * AddMeasurementModal — create/edit a body measurement (nutrition module).
 *
 * `measurement` present = edit mode. Every field is optional but at least one
 * must be filled; the ranges mirror MEASUREMENT_FIELDS so the coach gets an
 * instant Hebrew error, while the server stays the single source of truth.
 *
 * Clearing a field submits an empty string on purpose — updateMeasurement
 * treats "" as "remove this field from the document", so a stale value stops
 * drawing on the chart.
 *
 * bmi / bmr / rmr are derived server-side. BMI and BMR can be overridden by
 * hand (Round 3 / Phase 20): the optional "עריכה ידנית" inputs travel as
 * `metricOverrides` — a number sets the manual value, an empty string clears
 * it back to the computed one.
 */

import { useMemo, useState } from "react";
import { CaretDown, Check, PencilSimple, Ruler } from "@phosphor-icons/react";

import { MEASUREMENT_FIELDS } from "../../../firebase";
import { METRIC_OVERRIDE_FIELDS } from "../../../utils/nutritionCalc";
import HoverTip from "../../ui/HoverTip";
import { formatHebrewDate } from "../../../utils/dateUtils";
import DatePicker from "../../ui/DatePicker";
import Input from "../../ui/Input";
import ModalCloseButton from "../../ui/ModalCloseButton";
import "./AddMeasurementModal.css";
import LoaderLoop from "../../shared/loader/LoaderLoop";

const FIELD_KEYS = Object.keys(MEASUREMENT_FIELDS);
const OVERRIDE_KEYS = Object.keys(METRIC_OVERRIDE_FIELDS);

/** Today in Israel time, as the YYYY-MM-DD string DatePicker speaks. */
const todayIso = () => formatHebrewDate(new Date(), "yyyy-MM-dd");

/**
 * Prefill from an existing document.
 *
 * `fatPercentage` is skipped when the server estimated it (from waist + neck +
 * height): prefilling it would silently promote an estimate into a measured
 * value on the next save.
 */
const formFromMeasurement = (measurement) => {
  const form = { date: todayIso() };
  FIELD_KEYS.forEach((key) => {
    form[key] = "";
  });
  if (!measurement) return form;

  form.date = measurement.date
    ? formatHebrewDate(measurement.date, "yyyy-MM-dd")
    : todayIso();

  FIELD_KEYS.forEach((key) => {
    if (key === "fatPercentage" && measurement.fatPercentageEstimated === true) {
      return;
    }
    const value = measurement[key];
    if (value === undefined || value === null || value === "") return;
    form[key] = String(value);
  });

  return form;
};

/** Prefill the manual overrides only when the stored row is actually manual. */
const overridesFromMeasurement = (measurement) => {
  const out = {};
  OVERRIDE_KEYS.forEach((key) => {
    out[key] =
      measurement?.[`${key}Manual`] === true && measurement?.[key] !== undefined
        ? String(measurement[key])
        : "";
  });
  return out;
};

const parseField = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return null;
  const num = Number(String(raw).trim());
  return Number.isFinite(num) ? num : NaN;
};

/** Client-side mirror of sanitizeMeasurementValues, with Hebrew messages. */
const validateForm = (form) => {
  const errors = {};
  let filled = 0;

  FIELD_KEYS.forEach((key) => {
    const spec = MEASUREMENT_FIELDS[key];
    const value = parseField(form[key]);
    if (value === null) return;
    if (Number.isNaN(value)) {
      errors[key] = "ערך לא תקין";
      return;
    }
    if (value < spec.min || value > spec.max) {
      errors[key] = `הערך חייב להיות בין ${spec.min} ל-${spec.max} ${spec.unit}`;
      return;
    }
    filled += 1;
  });

  OVERRIDE_KEYS.forEach((key) => {
    const spec = METRIC_OVERRIDE_FIELDS[key];
    const value = parseField(form.overrides?.[key]);
    if (value === null) return;
    if (Number.isNaN(value)) {
      errors[`override_${key}`] = "ערך לא תקין";
      return;
    }
    if (value < spec.min || value > spec.max) {
      errors[`override_${key}`] = `הערך חייב להיות בין ${spec.min} ל-${spec.max}`;
    }
  });

  if (!form.date) {
    errors.date = "יש לבחור תאריך מדידה";
  }

  if (filled === 0 && Object.keys(errors).length === 0) {
    errors.general = "יש למלא לפחות מדידה אחת";
  }

  return errors;
};

function AddMeasurementModal({ measurement = null, isSaving, onClose, onSubmit }) {
  const isEdit = !!measurement;
  const [form, setForm] = useState(() => ({
    ...formFromMeasurement(measurement),
    overrides: overridesFromMeasurement(measurement),
  }));
  const [errors, setErrors] = useState({});
  const [showOverrides, setShowOverrides] = useState(() =>
    OVERRIDE_KEYS.some((key) => measurement?.[`${key}Manual`] === true)
  );

  const setOverride = (key, value) => {
    setForm((prev) => ({ ...prev, overrides: { ...prev.overrides, [key]: value } }));
    setErrors((prev) => {
      if (!prev[`override_${key}`]) return prev;
      const next = { ...prev };
      delete next[`override_${key}`];
      return next;
    });
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key] && !prev.general) return prev;
      const next = { ...prev };
      delete next[key];
      delete next.general;
      return next;
    });
  };

  const showsEstimatedHint = useMemo(
    () => isEdit && measurement?.fatPercentageEstimated === true,
    [isEdit, measurement]
  );

  const handleSave = () => {
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    // Send every field — an empty string tells the server to drop it.
    const data = { date: form.date, metricOverrides: {} };
    FIELD_KEYS.forEach((key) => {
      const raw = String(form[key] ?? "").trim();
      data[key] = raw === "" ? "" : Number(raw);
    });
    // Same rule for the overrides: "" clears a manual value back to computed.
    OVERRIDE_KEYS.forEach((key) => {
      const raw = String(form.overrides?.[key] ?? "").trim();
      data.metricOverrides[key] = raw === "" ? "" : Number(raw);
    });

    onSubmit(data);
  };

  return (
    <div className="nmm-overlay" onClick={onClose} role="presentation">
      <div
        className="nmm-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "עריכת מדידה" : "הוספת מדידה"}
      >
        <div className="nmm-header">
          <div className="nmm-title">
            <span className="nmm-title-icon">
              <Ruler size={20} weight="fill" />
            </span>
            <div className="nmm-title-text">
              <span className="nmm-eyebrow">מעקב תזונתי</span>
              <h2>{isEdit ? "עריכת מדידה" : "הוספת מדידה"}</h2>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="nmm-body">
          <p className="nmm-note">
            כל השדות אופציונליים — מלאו את מה שנמדד בפועל. ערכי BMI, BMR ו-RMR
            מחושבים אוטומטית; אפשר לדרוס BMI ו-BMR ידנית בהמשך הטופס.
          </p>

          <div className="nmm-field">
            <span className="nmm-label">תאריך המדידה</span>
            <DatePicker
              name="date"
              value={form.date}
              error={!!errors.date}
              onChange={(event) => setField("date", event.target.value)}
            />
            {errors.date && <span className="nmm-error">{errors.date}</span>}
          </div>

          {showsEstimatedHint && (
            <p className="nmm-note nmm-note-warning">
              אחוז השומן במדידה זו חושב אוטומטית מהיקף המותניים, הצוואר והגובה.
              הזנת ערך כאן תחליף את החישוב בערך שנמדד בפועל.
            </p>
          )}

          <div className="nmm-grid">
            {FIELD_KEYS.map((key) => {
              const spec = MEASUREMENT_FIELDS[key];
              return (
                <Input
                  key={key}
                  type="number"
                  lang="en"
                  inputMode="decimal"
                  step="0.1"
                  min={spec.min}
                  max={spec.max}
                  name={key}
                  label={`${spec.label} (${spec.unit})`}
                  value={form[key]}
                  onChange={(event) => setField(key, event.target.value)}
                  error={errors[key]}
                  placeholder={`${spec.min}–${spec.max}`}
                />
              );
            })}
          </div>

          {errors.general && <p className="nmm-error">{errors.general}</p>}

          {/* ----- manual overrides of computed metrics ----- */}
          <div className="nmm-overrides">
            <button
              type="button"
              className={`nmm-overrides-toggle ${showOverrides ? "open" : ""}`}
              onClick={() => setShowOverrides((v) => !v)}
              aria-expanded={showOverrides}
            >
              <PencilSimple size={14} weight="bold" />
              <span>עריכה ידנית של ערכים מחושבים (BMI / BMR)</span>
              <CaretDown size={13} weight="bold" className="nmm-overrides-caret" />
            </button>
            {showOverrides && (
              <div className="nmm-overrides-body">
                <p className="nmm-note">
                  ערך שתזינו כאן גובר על החישוב ולא יתעדכן כשמשנים את המדידות. הערך
                  המחושב נשמר ברקע — השאירו ריק כדי לחזור אליו.
                </p>
                <div className="nmm-grid">
                  {OVERRIDE_KEYS.map((key) => {
                    const spec = METRIC_OVERRIDE_FIELDS[key];
                    const isManual = measurement?.[`${key}Manual`] === true;
                    return (
                      <div className="nmm-override-field" key={key}>
                        <Input
                          type="number"
                          lang="en"
                          inputMode="decimal"
                          step={key === "bmi" ? "0.1" : "1"}
                          min={spec.min}
                          max={spec.max}
                          name={`override_${key}`}
                          label={spec.unit ? `${spec.label} (${spec.unit})` : spec.label}
                          value={form.overrides?.[key] ?? ""}
                          onChange={(event) => setOverride(key, event.target.value)}
                          error={errors[`override_${key}`]}
                          placeholder="מחושב אוטומטית"
                        />
                        {isManual && measurement?.[`${key}Computed`] != null && (
                          <HoverTip text="הערך שהנוסחה חישבה מהמדידות. נשמר גם כשמזינים ערך ידני.">
                            <span className="nmm-override-computed">
                              מחושב: {measurement[`${key}Computed`]}
                            </span>
                          </HoverTip>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="nmm-footer">
          <button type="button" className="nmm-secondary-btn" onClick={onClose}>
            ביטול
          </button>
          <button
            type="button"
            className="nmm-primary-btn"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <LoaderLoop size="xs" />
                <span>שומר...</span>
              </>
            ) : (
              <>
                <Check size={15} weight="bold" />
                <span>{isEdit ? "שמירת שינויים" : "שמירת המדידה"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddMeasurementModal;
